<template>
  <div>
    <v-container fluid>
      <v-row>
        <v-col md="4">
          <v-row>
            <v-col>
              <v-card shrink height="75vh">
                <v-toolbar flat color="grey lighten-3">
                  <v-toolbar-title>
                    {{ userGroupLabel ? $t('users.label.usersOfGroup', {group: userGroupLabel}) : $t('users.label.users') }}
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-tooltip v-if="groupFilter" bottom open-delay="600">
                    <template v-slot:activator="{ on }">
                      <v-btn
                          id="btn_new_user"
                          v-on="on"
                          icon
                          @click="groupFilter = null"
                          color="primary"
                      >
                        <v-icon>mdi-filter-off</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('users.label.resetGroupFilter') }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="groupFilter || !canWriteAnyGroup" bottom open-delay="600">
                    <template v-slot:activator="{ on }">
                      <v-btn
                          id="btn_new_user"
                          v-on="on"
                          icon
                          @click="newUser()"
                          color="primary"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('users.label.createUser') }}</span>
                  </v-tooltip>
                </v-toolbar>
                <v-list max-height="65vh" style="overflow-y: auto">
                  <v-list-item-group v-model="selectedUserIndex" color="primary">
                    <v-list-item
                        v-for="(user, i) in users"
                        :key="i"
                        @mouseenter="overUser = user.username"
                        @mouseleave="overUser = null"
                        v-show="groupFilter === null || user.defaultGroup === groupFilter"
                        :style="`background-color: ${user && user.disabled && selectedUserIndex !== i ? '#EFEFEF' : ''}`"
                    >
                      <v-list-item-content>
                        <v-list-item-title v-text="user.username"></v-list-item-title>
                        <v-list-item-subtitle v-text="user.defaultGroupLabel"></v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-tooltip bottom open-delay="600">
                          <template v-slot:activator="{ on }">
                            <v-btn
                                id="btn_delete_user"
                                v-on="on"
                                icon
                                @click.stop="maybeDeleteUser(user)"
                                color="primary"
                                :style="overUser === user.username ? '' : 'visibility: hidden'"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('users.label.deleteUser') }}</span>
                        </v-tooltip>
                      </v-list-item-action>
<!--                      <v-list-item-icon :style="overUser === user.username ? '' : 'visibility: hidden'">-->
<!--                        <v-icon>mdi-delete</v-icon>-->
<!--                      </v-list-item-icon>-->
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-if="selectedUser || this.createUser">
          <v-row>
            <v-col>
              <v-card shrink>
                <v-toolbar flat color="grey lighten-3">
                  <v-toolbar-title>
                    {{ createUser ? $t('users.label.newUser', {group: newUserGroupLabel}) : $t('users.label.user') }}
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-tooltip bottom :open-delay="tooltipOpenDelay">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <v-switch
                            v-model="selectedUserEnabled"
                            class="mt-5"
                            :loading="userDisabledLoading"
                        ></v-switch>
                      </div>
                    </template>
                    <span>
                      {{ $t(user && user.disabled ? 'users.label.enableUserAccount' : 'users.label.disableUserAccount') }}
                    </span>
                  </v-tooltip>
                  <TooltipIconButton
                      v-if="!editionMode"
                      id="btn_edit_user"
                      icon="mdi-pencil"
                      color="primary"
                      :tooltip="$t('users.label.edit')"
                      @click="editUser()"
                  ></TooltipIconButton>
                  <div v-if="editionMode">
                    <v-btn
                        id="btn_cancel_user"
                        text
                        @click="cancelEdition()"
                    >
                      {{ $t('app.button.cancel') }}
                    </v-btn>
                    <v-btn
                        id="btn_save_user"
                        color="primary"
                        @click="saveUser()"
                        :disabled="!validUserData"
                    >
                      {{ $t('app.button.save') }}
                    </v-btn>
                  </div>
                </v-toolbar>
                <v-form ref="form" v-model="validUserData">
                  <v-container>
                    <v-row>
                      <v-col id="user_username" md="4">
                        <v-text-field
                            v-model="userUsername"
                            :rules="labelRules"
                            :label="$t('users.label.username')"
                            :readonly="!createUser"
                            :disabled="editionMode && !createUser"
                            :class="createUser ? '' : 'removeBorder'"
                        ></v-text-field>
                      </v-col>
                      <v-col id="user_profileName" md="4">
                        <v-text-field
                            v-if="!createUser"
                            v-model="userProfilename"
                            :rules="labelRules"
                            :label="$t('users.label.profileName')"
                            :readonly="!editionMode || createUser"
                            :class="editionMode && !createUser ? '' : 'removeBorder'"
                        ></v-text-field>
                      </v-col>
                      <v-col id="user_group" md="4">
                        <v-text-field
                            v-if="!createUser"
                            v-model="user.defaultGroupLabel"
                            :label="$t('users.label.group')"
                            :disabled="editionMode"
                            readonly
                            class="removeBorder"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row v-if="editionMode">
                      <v-col id="user_password" md="4">
                        <v-text-field
                            v-if="!editionMode"
                            type="password"
                            value="xxxxxxxxxxx"
                            :label="$t('users.label.password')"
                            readonly
                            class="removeBorder"
                        ></v-text-field>
                        <v-text-field
                            v-else
                            v-model="newPassword"
                            :rules="[...passwordRules]"
                            :label="$t('users.label.password')"
                            :readonly="!editionMode"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="showPassword ? 'text' : 'password'"
                            @click:append="showPassword = !showPassword"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row v-if="editionMode">
                      <v-col id="user_password_confirm" md="4">
                        <v-text-field
                            v-model="passwordConfirm"
                            :rules="[...passwordRules, samePasswords]"
                            :label="$t('users.label.passwordConfirm')"
                            :readonly="!editionMode"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="showPassword ? 'text' : 'password'"
                            @click:append="showPassword = !showPassword"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row v-if="!createUser">
                      <v-col id="user_createdBy" md="4">
                        <v-text-field
                            v-model="maybeCreatedBy"
                            :label="$t('app.label.createdBy')"
                            readonly
                            :disabled="editionMode"
                            :class="editionMode ? '' : 'removeBorder'"
                        ></v-text-field>
                      </v-col>
                      <v-col id="user_createdAt" md="4">
                        <v-text-field
                            v-model="humanMaybeCreatedAt"
                            :label="$t('app.label.createdDate')"
                            readonly
                            :disabled="editionMode"
                            :class="editionMode ? '' : 'removeBorder'"
                        ></v-text-field>
                      </v-col>
<!--                      <v-col id="user_createdBy">{{$t('app.label.createdBy') + $t('colon') + selectedUser.createdBy}}</v-col>-->
<!--                      <v-col id="user_createdAt">{{$t('app.label.createdDate') + $t('colon') + moment(selectedUser.createdAt).fromNow() }}</v-col>-->
                    </v-row>
                  </v-container>
                </v-form>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-if="!createUser">
            <v-col>
              <v-card shrink height="45vh">
                <v-toolbar flat color="grey lighten-3">
                  <v-toolbar-title>{{ $t('users.label.rolesOf', {user: selectedUser.username}) }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-tooltip v-if="!rolesEditMode" bottom open-delay="600">
                    <template v-slot:activator="{ on }">
                      <v-btn
                          id="btn_edit_roles"
                          v-on="on"
                          icon
                          @click="editRoles()"
                          color="primary"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('users.label.edit') }}</span>
                  </v-tooltip>
                  <div v-if="rolesEditMode">
                    <v-btn
                        id="btn_cancel_roles"
                        text
                        @click="cancelRoles()"
                    >
                      {{ $t('app.button.cancel') }}
                    </v-btn>
                    <v-btn
                        id="btn_save_roles"
                        color="primary"
                        @click="saveRoles()"
                        :disabled="!validUserData"
                    >
                      {{ $t('app.button.save') }}
                    </v-btn>
                  </div>
                </v-toolbar>
                <RoleSelector
                    ref="RoleSelector"
                    v-if="selectedUser"
                    :target="selectedUser"
                    :userGroup="userGroup"
                    :roleSelector="roleSelector"
                    :rolesEditMode="rolesEditMode"
                ></RoleSelector>
              </v-card>
            </v-col>
            <v-col>
              <v-card shrink height="45vh">
                <v-toolbar flat color="grey lighten-3">
                  <v-toolbar-title>{{ $t('users.label.projectsOf', {user: selectedUser.username}) }}</v-toolbar-title>
                </v-toolbar>
                <v-list max-height="35vh" style="overflow-y: auto">
                  <v-list-item-group v-model="selectedProjectIndex" color="primary">
                    <v-list-item v-for="(project, i) in userProjects" :key="i">
                      <v-list-item-content>
                        <v-list-item-title v-text="project.label"></v-list-item-title>
                        <v-list-item-subtitle>{{ $t('app.label.createdBy') + " " + project.createdBy }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <DeleteConfirmDialog
        v-if="confirmDeleteUser"
        idHint="user"
        :title="$t('users.label.confirmDeleteTitle')"
        :html="deletedUser != null ? $t('users.label.confirmDeleteMessage', {username: this.deletedUser.username}) : ''"
        @yes="doDeleteUser()"
        @no="dontDeleteUser()"
    ></DeleteConfirmDialog>
  </div>
</template>

<script>
import TooltipIconButton from "../utils/TooltipIconButton";
import RoleSelector from "./RoleSelector";
import DeleteConfirmDialog from "../utils/DeleteConfirmDialog";
import vueAuth from '../../auth';
import {
  openUserAdmin,
  openUserAdminAndSelectUser,
  openUserAdminAndSelectGroupAndUser,
  openUserAdminAndSelectGroup
} from "@/routes/userAdminRoutes";
import {mapState, mapGetters, mapMutations, mapActions} from "vuex";
import {mapProperty} from "@/utils/vuexx";
import moment from "moment";
import {decodeParams} from "@/routes/pathParams";

export default {
  components: {
    TooltipIconButton,
    RoleSelector,
    DeleteConfirmDialog,
  },
  data() {
    return {
      deletedUser: null,
      confirmDeleteUser: false,
      editMode: false,
      rolesEditMode: false,
      createUser: false,
      selectedUserIndex: null,
      selectedRoleIndex: null,
      selectedProjectIndex: null,
      overUser: null,
      newUserUsername: null,
      newUserProfilename: null,
      password: "",
      passwordChanged: false,
      passwordConfirm: "",
      editionMode: false,
      validUserData: true,
      showPassword: false,
      userDisabledLoading: false,
      labelRules: [
        v => !!v || this.$i18n.t("users.label.nonEmptyUsername"),
        v =>
            (v && v.length <= 40) ||
            this.$i18n.t("createProject.errors.nameLength", {count: 40})
      ],
      passwordRules: [
        v => !!v || this.$t("app.message.required"),
        v =>
            v.length >= this.minPasswordLength ||
            this.$t("app.message.minlengthpassword", {length: this.minPasswordLength})
      ],
      samePasswords: v => v === this.password || this.$t("app.message.passwordsmustmatch"),
    }
  },
  mounted() {
    const self = this
    const mount = async function () {
      // workaround strange behavior: 
      // when "admin" clicks on a group in "Groups.vue" then signout
      // its seems like Vue.js is temporarily mounting this (lazy-loaded?) component 
      // so checking if the user is logged-in
      let userLoggedIn = vueAuth.isAuthenticated();
      // try {
      //   await self.getUser()
      //   userLoggedIn = true;
      // } catch (error) {
      //   userLoggedIn = false
      // }
      if (userLoggedIn) {
        if (self.loggedUser) {
          self.setLoggedUser(self.loggedUser)
        }
        self.selectedUserIndex = null
        await self.getRoles()
        await self.getUsers()
        self.selectUserByParams(self.$route.params.selectionParams)
      }
    }
    mount()
  },
  watch: {
    '$route.params.selectionParams': function (value) {
      this.selectUserByParams(value)
    },
    selectedUserIndex: function (value) {
      // use routing functions to track changes in history
      if (value != null) {
        const selectedUser = this.users[value]
        if (this.groupFilter == null)
          openUserAdminAndSelectUser(selectedUser.username)
        else
          openUserAdminAndSelectGroupAndUser(this.groupFilter, selectedUser.username)
      } else {
        if (this.groupFilter)
          openUserAdminAndSelectGroup(this.groupFilter)
        else
          openUserAdmin(null)
      }
    },
    user: function (value) {
      if (!value)
        this.selectedUserIndex = null
      else {
        this.cancelEdition()
        this.cancelRoles()
      }
    },
    loggedUser: function (value) {
      // workaround to app init mess: auth/user store value is set after this component's mount
      this.setLoggedUser(value)
    }
  },
  computed: {
    ...mapState(["uiConfig", "tooltipOpenDelay"]),
    minPasswordLength() {
      return this.uiConfig.passwordPolicy.minLength
    },
    ...mapState("auth", {loggedUser: "user"}),
    ...mapGetters("auth", ["isPlatformUserAdmin", "canWriteAnyGroup"]),
    ...mapState("usersGroups", ["groups"]),
    ...mapState("users", [
      "users",
      "groupNameFilter",
      "user",
      "roles",
      "userProjects",
      "roleSelector",
    ]),
    selectedUser: mapProperty("users", "user"),
    groupFilter: mapProperty("users", "groupNameFilter"),
    selectedUserEnabled: {
      get() {
        return !this.user?.disabled
      },
      set() {
        this.toggleUserDisabledWithLoading(this.user)
      }
    },
    userGroup() {
      const filteredGroups = this.groups.filter(g => g.name === this.user.defaultGroup)
      const group = filteredGroups.length === 1 ? filteredGroups[0] : null;
      return group
    },
    newUserGroupLabel() {
      const loggedUserGroup = this.loggedUser ? this.loggedUser.defaultGroup : null
      const groupName = this.groupFilter ? this.groupFilter : loggedUserGroup;
      const filteredGroups = this.groups.filter(g => g.name === groupName)
      return filteredGroups.length === 1 ? filteredGroups[0].label : groupName;
    },
    userGroupLabel() {
      if (this.groupFilter == null)
        return null
      const filteredGroups = this.groups.filter(g => g.name === this.groupFilter)
      return filteredGroups.length === 1 ? filteredGroups[0].label : this.groupFilter;
    },
    maybeCreatedBy() {
      return this.selectedUser ? (this.selectedUser.createdBy ? this.selectedUser.createdBy : "-") : ""
    },
    humanMaybeCreatedAt() {
      moment.locale(this.$i18n.locale);
      return this.selectedUser ? (this.selectedUser.createdAt ? moment(this.selectedUser.createdAt).fromNow() : "-") : ""
    },
    newPassword: {
      get() {
        return this.password
      },
      set(value) {
        this.password = value
        this.passwordConfirm = ""
        this.passwordChanged = true
      }
    },
    userUsername: {
      get() {
        return this.createUser ? this.newUserUsername : (this.selectedUser ? this.selectedUser.username : "")
      },
      set(value) {
        this.newUserProfilename = value
        this.newUserUsername = value
      }
    },
    userProfilename: {
      get() {
        return this.selectedUser ? (this.editionMode ? this.newUserProfilename : this.selectedUser.profilename) : ""
      },
      set(value) {
        this.newUserProfilename = value
      }
    },
  },
  methods: {
    ...mapMutations("users", [
      "setUser",
      "setGroupNameFilter",
      "setLoggedUser",
    ]),
    ...mapActions("users", [
      "getUsers",
      "getUser",
      "getRoles",
      "getUserProjects",
      "updateUser",
      "updateUserProfile",
      "addUser",
      "deleteUser",
      "toggleUserDisabled",
    ]),
    ...mapActions("usersGroups", ["getGroups"]),
    async selectUserByParams(params) {
      // this is required because of the UserAdmin tabs (sharing the same route)
      if (!this.$route.path.startsWith("/admin/users"))
        return
      await this.getGroups()
      if (params) {
        const selectionParams = decodeParams(params)
        this.selectUser(selectionParams.username ? selectionParams.username : null)
        this.groupFilter = selectionParams.groupName ? selectionParams.groupName : null
      } else {
        this.selectUser(null)
        this.groupFilter = null
      }
    },
    async selectUser(username) {
      if (username) {
        // get user
        const user = await this.getUser(username)
        this.newUserProfilename = user.profilename
        this.newUserUsername = user.username
        // get user projects (don't await for result)
        this.getUserProjects(username)
        // init roles panel (don't await for result)
        //this.initRoleDataFromStore()
        const index = this.users.findIndex(user => user.username === username)
        // also select the user in the list 
        // but don't do it (to prevent watch loop?) if it is already the case
        if (index !== -1 && this.selectedUserIndex !== index)
          this.selectedUserIndex = index
      } else {
        this.selectedUser = null
        this.newUserProfilename = null
        this.newUserUsername = null
        // also unselect the user in the list 
        // but don't do it (to prevent watch loop?) if it is already the case
        if (this.selectedUserIndex != null)
          this.selectedUserIndex = null
      }
    },
    newUser() {
      this.createUser = true
      this.editionMode = true
      this.newUserUsername = ""
      this.newUserProfilename = ""
      // const self = this
      // setTimeout(() => {
      //   self.$refs.form.validate()        
      // }, 1000)

    },
    maybeDeleteUser(user) {
      this.deletedUser = user
      this.confirmDeleteUser = true
    },
    dontDeleteUser() {
      this.deletedUser = null
      this.confirmDeleteUser = false
    },
    doDeleteUser() {
      try {
        this.deleteUser(this.deletedUser.username)
      } finally {
        this.confirmDeleteUser = false
      }
    },
    editUser() {
      this.editionMode = true
      this.password = null
      this.passwordConfirm = null
      this.newUserProfilename = this.selectedUser.profilename
    },
    cancelEdition() {
      this.editionMode = false
      this.createUser = false
      this.password = ""
      this.passwordConfirm = ""
      this.passwordChanged = false
      this.newUserUsername = ""
      this.newUserProfilename = ""
    },
    async saveUser() {
      let success = false
      try {
        if (this.createUser) {
          await this.addUser({username: this.newUserUsername, password: this.password, roles: [], permissions: []})
        } else {
          const update = {}
          if (this.newUserProfilename !== this.user.profilename)
            update.profilename = this.newUserProfilename
          if (this.passwordChanged)
            update.password = this.password
          if (Object.keys(update).length !== 0)
            await this.updateUserProfile({username: this.selectedUser.username, update})
        }
        success = true
      } finally {
        try {
          if (success) {
            if (this.createUser) {
              await this.getUsers();
              await this.getUser(this.newUserUsername)
              const self = this
              setTimeout(() => {
                const index = self.users.findIndex(user => user.username === this.newUserUsername)
                if (index !== -1)
                  self.selectedUserIndex = index

              }, 500)
            } else {
              await this.getUser(this.selectedUser.username)
            }
          }
        } finally {
          this.cancelEdition()
        }
      }
    },
    editRoles() {
      this.rolesEditMode = true
    },
    cancelRoles() {
      if (this.rolesEditMode) {
        this.rolesEditMode = false
        //this.initRoleDataFromStore()
      }
    },
    async saveRoles() {
      const selectedSystemRoles = [...this.$refs["RoleSelector"].selectedSystemRoles]
      const selectedFunctionalRoles = [...this.$refs["RoleSelector"].selectedFunctionalRoles]
      this.rolesEditMode = false
      const roles = []
      for (const i of selectedSystemRoles)
        roles.push(this.roleSelector.groupSystemRoles[i].rolename)
      for (const i of selectedFunctionalRoles)
        roles.push(this.roleSelector.groupFunctionalRoles[i].rolename)
      // save new roles
      await this.updateUser({username: this.selectedUser.username, update: {roles}})
      // get new roles from api (to be sure)
      await this.getUser(this.selectedUser.username)
      // init roles panel (don't await for result)
      //this.initRoleDataFromStore()

    },
    async toggleUserDisabledWithLoading(user) {
      this.userDisabledLoading = true
      try {
        await this.toggleUserDisabled(user)
      } finally {
        this.userDisabledLoading = false
      }
    },
    // initRoleDataFromStore() {
    //   this.selectedSystemRoles.splice(0, this.selectedSystemRoles.length)
    //   this.selectedFunctionalRoles.splice(0, this.selectedFunctionalRoles.length)
    //   for (const roleIndex of this.userSystemRoles) 
    //     this.selectedSystemRoles.push(roleIndex)
    //   for (const roleIndex of this.userFunctionalRoles) 
    //     this.selectedFunctionalRoles.push(roleIndex)
    // },
  }
}
</script>

<style>
.removeBorder > .v-input__control > .v-input__slot::after {
  border-style: none !important;
}

.removeBorder > .v-input__control > .v-input__slot::before {
  border-style: none !important;
}
</style>